// KenoComponent.js with persistent highlighting and fixed animations
import React, { useState, useEffect } from 'react';
import { parseEther } from 'ethers';
import { createPortal } from 'react-dom';

import KenoService from '../services/KenoService';
import web3Service from '../services/Web3Service';
import { KENO_MIN_BET_AMOUNT } from '../config';

/* global BigInt */  // This tells ESLint that BigInt is defined globally

const KenoComponent = () => {
    // Game state
    const [betAmount, setBetAmount] = useState(KENO_MIN_BET_AMOUNT.toString());
    const [vrfFee, setVrfFee] = useState('0');
    const [totalAmount, setTotalAmount] = useState(KENO_MIN_BET_AMOUNT.toString());
    const [selectedNumbers, setSelectedNumbers] = useState([]);
    const [isWaiting, setIsWaiting] = useState(false);
    const [hasPending, setHasPending] = useState(false);
    const [requestId, setRequestId] = useState(null);
    const [animating, setAnimating] = useState(false); // Animation control state

    // Result states
    const [showResult, setShowResult] = useState(false);
    const [resultData, setResultData] = useState(null);
    const [bets, setBets] = useState([]);

    // New state for highlighting numbers after a game - separate from resultData
    const [lastGameResult, setLastGameResult] = useState(null);

    // Contract data
    const [houseBalance, setHouseBalance] = useState('0');
    const [maxBet, setMaxBet] = useState('0');
    const [isOwner, setIsOwner] = useState(false);
    const [payoutTable, setPayoutTable] = useState([]);

    // Owner controls
    const [liquidityAmount, setLiquidityAmount] = useState('0.1');
    const [withdrawAmount, setWithdrawAmount] = useState('0.1');

    // Initialize Keno service
    useEffect(() => {
        const initKenoService = async () => {
            // Check if web3Service is connected
            if (!web3Service.isConnected()) {
                console.log('Web3Service is not connected yet');
                return;
            }

            try {
                // Initialize Keno service
                await KenoService.initialize();

                // Load initial data
                await loadInitialData();
            } catch (error) {
                console.error('Error initializing Keno service:', error);
            }
        };

        initKenoService();

        // Set up a check every second if not connected initially
        const checkInterval = setInterval(() => {
            if (web3Service.isConnected() && !KenoService.contract) {
                initKenoService();
            }
        }, 1000);

        // Cleanup interval
        return () => clearInterval(checkInterval);
    }, []);

    // Periodically check pending bet status (similar to CoinFlip implementation)
    useEffect(() => {
        if (!hasPending || !KenoService.contract || !KenoService.account) return;

        const checkPending = async () => {
            try {
                console.log('🔍 Checking pending Keno bet status...');
                const isPending = await KenoService.hasPendingBet();

                if (hasPending && !isPending) {
                    // Bet was settled but we might have missed the event
                    if (requestId) {
                        // Look for the settled event in past events
                        const ourEvent = await KenoService.getPastBetSettledEvents(requestId);

                        if (ourEvent) {
                            console.log('✅ Found previously missed settled event:', ourEvent);
                            
                            // Process the bet result as if we just received the event
                            const result = processBetResult(ourEvent);

                            // Set animating flag to prevent state resets
                            setAnimating(true);
                            
                            // Save result for highlighting
                            setLastGameResult(result);
                            
                            // Show result animation
                            handleBetResult(result);

                            // Update bet history without duplicates
                            setBets(prev => {
                                const resultIdStr = result.requestId.toString();
                                if (prev.some(bet => bet.requestId.toString() === resultIdStr)) {
                                    return prev;
                                }
                                return [result, ...prev];
                            });
                            
                            // Reset states after animation completes
                            setTimeout(() => {
                                setIsWaiting(false);
                                setHasPending(false);
                                setRequestId(null);
                                setAnimating(false);
                                
                                // Refresh contract data
                                refreshContractData();
                            }, 11000); // 11 seconds to ensure modal has closed
                            
                            return; // Exit early to prevent state changes
                        }
                    }

                    // Only update these states if we're not currently animating a result
                    if (!animating) {
                        setIsWaiting(false);
                        setHasPending(false);
                        setRequestId(null);
                    }

                    // Refresh contract data
                    refreshContractData();
                } else if (isPending !== hasPending) {
                    setHasPending(isPending);
                }
            } catch (error) {
                console.error('❌ Error checking pending bet:', error);
            }
        };

        // Check immediately and then set up interval
        checkPending();
        const interval = setInterval(checkPending, 3000);

        return () => clearInterval(interval);
    }, [hasPending, requestId, animating]);

    // Load initial contract data
    const loadInitialData = async () => {
        try {
            // Get payout table
            const payouts = await KenoService.loadPayoutTable();
            setPayoutTable(payouts);

            // Get contract data
            const { balance, max, vrfFee: fee } = await KenoService.getContractData();
            setHouseBalance(balance);
            setMaxBet(max);
            setVrfFee(fee);
            updateTotalAmount(betAmount, fee);

            // Check if user has pending bet
            const pendingBet = await KenoService.hasPendingBet();
            setHasPending(pendingBet);

            // Check if user is owner
            const ownerStatus = await web3Service.checkIfOwner();
            console.log('Keno - Is User Owner?', ownerStatus);
            setIsOwner(ownerStatus);

            // Set up event listeners
            setupEventListeners();
        } catch (error) {
            console.error('Error loading initial data:', error);
        }
    };

    // Process a bet result event
    const processBetResult = (event) => {
        return {
            requestId: event.returnValues.requestId,
            amount: KenoService.fromWei(event.returnValues.amount),
            selectedNumbers: event.returnValues.selectedNumbers.map(Number),
            drawnNumbers: event.returnValues.drawnNumbers.map(Number),
            hits: Number(event.returnValues.hits),
            payout: KenoService.fromWei(event.returnValues.payout)
        };
    };

    // Set up event listeners
    const setupEventListeners = () => {
        console.log('Setting up event listeners for KenoComponent');
        
        // Only proceed if KenoService is properly initialized
        if (!KenoService.contract || !KenoService.account) {
          console.error('Cannot set up event listeners: KenoService not fully initialized');
          return;
        }
        
        // Set up BetPlaced event listener
        const handleBetPlaced = (event) => {
          console.log('🎲 Keno BetPlaced event received:', event);
          setRequestId(event.returnValues.requestId);
          setIsWaiting(true);
          setHasPending(true);
          // Clear any previous result
          setShowResult(false);
        };
      
        // Set up BetSettled event listener
        const handleBetSettled = (event) => {
          console.log('💰 Keno BetSettled event received:', event);
      
          // Check if we already processed this event to prevent duplicates
          const eventRequestId = event.returnValues.requestId.toString();
          if (bets.some(bet => bet.requestId.toString() === eventRequestId)) {
            console.log('Already processed this result, ignoring duplicate event');
            return;
          }
      
          // Process bet result
          const result = processBetResult(event);
      
          // Update bet history without duplicates
          setBets(prev => {
            // Convert requestIds to strings for comparison
            const requestIdStr = result.requestId.toString();
            if (prev.some(bet => bet.requestId.toString() === requestIdStr)) {
              return prev;
            }
            return [result, ...prev];
          });
      
          // Set animating flag before starting animation
          setAnimating(true);
      
          // Save the result for highlighting numbers - this persists after modal closes
          setLastGameResult(result);
      
          // Show result animation in modal
          handleBetResult(result);
      
          // Wait for animation to complete before resetting game state
          // This is important - don't reset state prematurely!
          setTimeout(() => {
            setIsWaiting(false);
            setHasPending(false);
            setRequestId(null);
            setAnimating(false); // Animation is complete
      
            // Refresh contract data
            refreshContractData();
      
            // We don't clear lastGameResult here - it persists until new bet
          }, 11000); // Set to 11 seconds to ensure modal has time to close (10s display + 1s buffer)
        };
      
        // Subscribe to events - store the result to track if subscription was successful
        const subscriptionSuccess = KenoService.subscribeToEvents(handleBetPlaced, handleBetSettled);
        console.log('Keno event subscription result:', subscriptionSuccess);
        
        // If subscription failed, set up a retry mechanism
        if (!subscriptionSuccess) {
          console.log('Will retry Keno event subscription in 3 seconds');
          setTimeout(() => {
            if (KenoService.contract && KenoService.account) {
              const retrySuccess = KenoService.subscribeToEvents(handleBetPlaced, handleBetSettled);
              console.log('Keno event subscription retry result:', retrySuccess);
            }
          }, 3000);
        }
      };

    // Calculate total bet amount including VRF fee
    const updateTotalAmount = (bet, fee) => {
        try {
            const betBigInt = window.BigInt(bet);
            const feeBigInt = window.BigInt(fee);
            setTotalAmount((betBigInt + feeBigInt).toString());
        } catch (error) {
            console.error('Error updating total amount:', error);
            // Fallback to regular number addition
            setTotalAmount((Number(bet) + Number(fee)).toString());
        }
    };

    // Refresh contract data (balance and max bet)
    const refreshContractData = async () => {
        try {
            const { balance, max } = await KenoService.getContractData();
            setHouseBalance(balance);
            setMaxBet(max);
        } catch (error) {
            console.error('Error refreshing contract data:', error);
        }
    };

    // Clear highlighting from previous games
    const clearHighlights = () => {
        // Only clear if not waiting for a bet result and not animating
        if (!hasPending && !isWaiting && !animating) {
            setLastGameResult(null);
        }
    };

    // Handle number selection (toggle)
    const handleNumberSelection = (number) => {
        if (hasPending || isWaiting || animating) return;

        // Clear previous game highlights when selecting a number
        clearHighlights();

        setSelectedNumbers(prev => {
            if (prev.includes(number)) {
                // Remove number if already selected
                return prev.filter(n => n !== number);
            } else if (prev.length < 10) {
                // Add number if less than 10 are selected
                return [...prev, number];
            }
            return prev;
        });
    };

    // Handle quick pick
    const handleQuickPick = () => {
        if (hasPending || isWaiting || animating) return;

        // Clear highlights when using Quick Pick
        clearHighlights();

        const generateNumbers = () => {
            const numbers = [];
            while (numbers.length < 10) {
                const num = Math.floor(Math.random() * 40) + 1;
                if (!numbers.includes(num)) {
                    numbers.push(num);
                }
            }
            return numbers.sort((a, b) => a - b);
        };

        setSelectedNumbers(generateNumbers());
    };

    // Handle clear selection
    const handleClearSelection = () => {
        if (hasPending || isWaiting || animating) return;
        setSelectedNumbers([]);
        // Clear last game result when starting fresh
        clearHighlights();
    };

    // Handle bet amount input change
    const handleBetAmountChange = (e) => {
        if (hasPending || isWaiting || animating) return;
        
        try {
            const newAmount = parseEther(e.target.value).toString();
            setBetAmount(newAmount);
            updateTotalAmount(newAmount, vrfFee);
        } catch (error) {
            console.error('Invalid bet amount:', error);
        }
    };

    // Calculate potential payouts based on current bet amount
    const calculatePotentialPayouts = () => {
        return payoutTable.map((multiplier, hits) => {
            const payout = Number(KenoService.fromWei(betAmount)) * multiplier;
            return { hits, multiplier, payout };
        });
    };

    // Handle bet result (win or loss)
    const handleBetResult = (result) => {
        console.log('🎯 handleBetResult called with:', result);

        // Store the result for the modal
        setResultData(result);

        // IMPORTANT: Store a separate copy for highlighting
        // This ensures lastGameResult won't be affected by any operations on resultData
        setLastGameResult({ ...result });

        // Show the result modal
        setShowResult(true);

        // Auto-hide the modal after 10 seconds
        setTimeout(() => {
            console.log('🎯 Timeout fired - hiding result modal');
            // Just hide the modal without affecting lastGameResult
            setShowResult(false);
            // Don't reset other states here!
        }, 10000);
    };

    // Update the closeModal function
    const closeModal = (e) => {
        // Stop event propagation to prevent any parent handlers from firing
        if (e) {
            e.stopPropagation();
        }

        // Only hide the modal, preserving all other state
        setShowResult(false);
    };

    // Place a new bet
    const placeBet = async () => {
        if (!KenoService.contract || !KenoService.account) {
            console.log('❌ Cannot place bet: contract or account not available');
            return;
        }

        // Validate selection
        if (selectedNumbers.length !== 10) {
            alert('You must select exactly 10 numbers');
            return;
        }
        
        // Don't allow placing a bet during animation
        if (animating) {
            alert('Please wait for the current animation to complete');
            return;
        }

        try {
            // Check if user already has a pending bet
            const isPending = await KenoService.hasPendingBet();
            if (isPending) {
                alert('You already have a pending bet');
                return;
            }

            // Clear all previous result data
            setLastGameResult(null);  // Explicitly clear highlight state
            setResultData(null);      // Clear modal result data

            // Place the bet
            setIsWaiting(true);
            const result = await KenoService.placeBet(selectedNumbers, totalAmount);

            if (result.success && result.requestId) {
                // Manually update state in case event listener missed it
                setRequestId(result.requestId);
                setHasPending(true);
            } else {
                // We still need to wait for the event
                setHasPending(true);
            }
        } catch (error) {
            console.error('❌ Error placing bet:', error);
            alert('Error placing bet: ' + error.message);

            // Reset state in case of error
            setIsWaiting(false);
        }
    };

    // Force check for pending bet status
    const checkBetStatus = async () => {
        if (!KenoService.contract || !KenoService.account) return;
        
        // Don't check while animating
        if (animating) {
            alert('Please wait for the current animation to complete');
            return;
        }

        try {
            const isPending = await KenoService.hasPendingBet();

            if (!isPending && hasPending) {
                if (!animating) {
                    // Only update if not in animation
                    setHasPending(false);
                    setIsWaiting(false);
                }
                refreshContractData();
                alert('Your bet has been settled!');
            } else {
                alert(isPending ? 'Your bet is still pending' : 'You have no pending bets');
            }
        } catch (error) {
            console.error('Error checking bet status:', error);
            alert('Error checking bet status');
        }
    };

    // Add liquidity (owner only)
    const addLiquidity = async () => {
        if (!KenoService.contract || !KenoService.account || !isOwner) return;
        
        // Don't allow during animation
        if (animating) {
            alert('Please wait for the current animation to complete');
            return;
        }

        try {
            const amountWei = parseEther(liquidityAmount).toString();
            const success = await KenoService.addLiquidity(amountWei);

            if (success) {
                alert('Liquidity added successfully');
                refreshContractData();
            }
        } catch (error) {
            console.error('Error adding liquidity:', error);
            alert('Error adding liquidity: ' + error.message);
        }
    };

    // Remove liquidity (owner only)
    const removeLiquidity = async () => {
        if (!KenoService.contract || !KenoService.account || !isOwner) return;
        
        // Don't allow during animation
        if (animating) {
            alert('Please wait for the current animation to complete');
            return;
        }

        try {
            const amountWei = parseEther(withdrawAmount).toString();
            const success = await KenoService.removeLiquidity(amountWei);

            if (success) {
                alert('Liquidity removed successfully');
                refreshContractData();
            }
        } catch (error) {
            console.error('Error removing liquidity:', error);
            alert('Error removing liquidity: ' + error.message);
        }
    };

    // Determine number class based on game state and history
    const getNumberClass = (number) => {
        // Check for lastGameResult highlighting first - highest priority
        if (lastGameResult) {
            // Hit - both selected and drawn (win)
            if (lastGameResult.drawnNumbers.includes(number) &&
                lastGameResult.selectedNumbers.includes(number)) {
                return 'last-game-hit';
            }
            // Drawn but not selected (missed opportunity)
            else if (lastGameResult.drawnNumbers.includes(number) &&
                !lastGameResult.selectedNumbers.includes(number)) {
                return 'last-game-drawn';
            }
            // Selected but not drawn (miss)
            else if (!lastGameResult.drawnNumbers.includes(number) &&
                lastGameResult.selectedNumbers.includes(number)) {
                return 'last-game-miss';
            }
        }

        // If not part of lastGameResult, use normal states
        if (selectedNumbers.includes(number)) {
            return 'selected';
        }

        // Animation states during active results
        if (showResult && resultData && resultData.drawnNumbers) {
            if (resultData.drawnNumbers.includes(number)) {
                if (resultData.selectedNumbers.includes(number)) {
                    return 'hit';
                }
                return 'drawn';
            }
        }

        return '';
    };

    return (
        <div className="keno-game">
            <div className="contract-info">
                <div>House Balance: {KenoService.fromWei(houseBalance)} BNB</div>
                <div>Max Bet: {KenoService.fromWei(maxBet)} BNB</div>
            </div>
            
            {/* Keno Board */}
            <div className="keno-board-container">
                <h2>Select 10 Numbers</h2>

                <div className="keno-board">
                    <div className="keno-board-header">
                        <div className="keno-board-title">KENO</div>
                        <div className="keno-board-subtitle">Match more numbers to win bigger prizes!</div>
                    </div>

                    {/* Added onClick handler to clear highlights when the grid is clicked */}
                    <div 
                        className={`keno-grid ${isWaiting ? 'waiting' : ''} ${animating ? 'animating' : ''}`}
                        onClick={clearHighlights}
                    >
                        {Array.from({ length: 40 }, (_, i) => i + 1).map(number => (
                            <div
                                key={number}
                                className={`keno-number ${getNumberClass(number)}`}
                                onClick={(e) => {
                                    // Stop propagation to prevent the grid's onClick from firing
                                    e.stopPropagation();
                                    handleNumberSelection(number);
                                }}
                            >
                                {number}
                            </div>
                        ))}
                    </div>

                    {lastGameResult && (
                      <div className="highlight-legend">
                        <div className="highlight-legend-item">
                          <span className="highlight-icon hit">✓</span>
                          <span>Matches</span>
                        </div>
                        <div className="highlight-legend-item">
                          <span className="highlight-icon miss">💣</span>
                          <span>Misses</span>
                        </div>
                        <div className="highlight-legend-item">
                          <span className="highlight-icon drawn">🔵</span>
                          <span>Picks</span>
                        </div>
                      </div>
                    )}
                    <div className="selection-controls">
                        <button
                            className="control-button"
                            onClick={handleQuickPick}
                            disabled={hasPending || isWaiting || animating}
                        >
                            Random
                        </button>
                        <button
                            className="control-button"
                            onClick={handleClearSelection}
                            disabled={hasPending || isWaiting || animating || selectedNumbers.length === 0}
                        >
                            Clear
                        </button>
                        <div className="selection-count">
                            {selectedNumbers.length}/10 Selected
                        </div>
                    </div>
                </div>

                <div className="keno-selection-display">
                    <div className="selected-numbers-container">
                        <div className="selected-numbers-label">Your Numbers:</div>
                        <div className="selected-numbers-list">
                            {selectedNumbers.length > 0 ? (
                                selectedNumbers.sort((a, b) => a - b).map(num => (
                                    <span key={num} className="selected-number-chip">{num}</span>
                                ))
                            ) : (
                                <span className="no-selections">Select 10 numbers to play</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showResult && resultData && Number(resultData.payout) > 0 &&
                createPortal(
                    <div className="result-modal win-modal" onClick={closeModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h2>YOU WON!</h2>
                            <div className="win-details">
                                <div>Matches: {resultData.hits}/10</div>
                                <div className="win-amount">{resultData.payout} BNB</div>
                            </div>
                            <div className="confetti-container">
                                {Array.from({ length: 50 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="confetti"
                                        style={{
                                            left: `${Math.random() * 100}%`,
                                            animationDelay: `${Math.random() * 5}s`,
                                            backgroundColor: ['#FCD34D', '#FBBF24', '#F59E0B', '#D97706', '#3B82F6', '#10B981'][Math.floor(Math.random() * 6)]
                                        }}
                                    ></div>
                                ))}
                            </div>
                            <button className="modal-button" onClick={(e) => closeModal(e)}>
                                Awesome!
                            </button>
                        </div>
                    </div>,
                    document.body
                )
            }

            {hasPending && !animating && (
                <div className="pending-bet">
                    <h3>Bet Status</h3>
                    <p>
                        Your bet is pending. Please wait for the result.
                    </p>
                    <button className="check-button" onClick={checkBetStatus}>
                        Check Status
                    </button>
                </div>
            )}

            <div className="betting-controls">
                <div className="bet-amount">
                    <label>Bet Amount (BNB):</label>
                    <input
                        type="number"
                        min="0.015"
                        step="0.001"
                        value={KenoService.fromWei(betAmount)}
                        onChange={handleBetAmountChange}
                        disabled={hasPending || isWaiting || animating}
                    />
                    <div className="min-bet">Minimum bet: 0.015 BNB</div>
                </div>

                <div className="bet-summary">
                    <div>VRF Fee: {KenoService.fromWei(vrfFee)} BNB</div>
                    <div className="total-cost">Total Cost: {KenoService.fromWei(totalAmount)} BNB</div>
                </div>

                <button
                    className="place-bet-button"
                    onClick={placeBet}
                    disabled={!KenoService.account || hasPending || isWaiting || animating || selectedNumbers.length !== 10}
                >
                    {hasPending ? 'Bet Pending...' : isWaiting ? 'Placing Bet...' : animating ? 'Animation...' : 'Place Bet'}
                </button>
            </div>

            {/* Owner Controls */}
            {isOwner && (
                <div className="owner-controls">
                    <h2>Owner Controls</h2>

                    <div className="owner-actions">
                        <div className="liquidity-control">
                            <label>Add Liquidity (BNB):</label>
                            <div className="input-with-button">
                                <input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    value={liquidityAmount}
                                    onChange={(e) => setLiquidityAmount(e.target.value)}
                                    disabled={animating}
                                />
                                <button onClick={addLiquidity} disabled={animating}>+</button>
                            </div>
                        </div>

                        <div className="liquidity-control">
                            <label>Remove Liquidity (BNB):</label>
                            <div className="input-with-button">
                                <input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    value={withdrawAmount}
                                    onChange={(e) => setWithdrawAmount(e.target.value)}
                                    disabled={animating}
                                />
                                <button className="remove-button" onClick={removeLiquidity} disabled={animating}>-</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Payout Table */}
            <div className="payout-table">
                <h3>Payout Table</h3>
                <table>
                    <thead>
                        <tr>
                            <th>Matches</th>
                            <th>Multiplier</th>
                            <th>Potential Payout</th>
                        </tr>
                    </thead>
                    <tbody>
                        {calculatePotentialPayouts().filter(p => p.multiplier > 0).map(({ hits, multiplier, payout }) => (
                            <tr key={hits} className={hits === resultData?.hits ? 'highlight-row' : ''}>
                                <td>{hits}</td>
                                <td>{multiplier}x</td>
                                <td>{payout.toFixed(3)} BNB</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {bets.length > 0 && (
                <div className="bet-history">
                    <h3>Bet History</h3>
                    <div className="history-list">
                        {bets.map((bet) => (
                            <div
                                key={bet.requestId.toString()}
                                className={`history-item ${Number(bet.payout) > 0 ? 'win' : 'lose'}`}
                            >
                                <div>Bet: {bet.amount} BNB</div>
                                <div>Matches: {bet.hits}/10</div>
                                <div>{Number(bet.payout) > 0 ? `Won ${bet.payout} BNB` : 'Lost'}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Loss Modal */}
            {showResult && resultData && Number(resultData.payout) === 0 &&
                createPortal(
                    <div className="result-modal lose-modal" onClick={closeModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h2>UNLUCKY!</h2>
                            <div className="lose-details">
                                <div>Matches: {resultData.hits}/10</div>
                                <div className="lose-amount">-{resultData.amount} BNB</div>
                            </div>
                            <button className="modal-button" onClick={(e) => closeModal(e)}>
                                Aww!
                            </button>
                        </div>
                    </div>,
                    document.body
                )
            }
        </div>
    );
};

export default KenoComponent;